import { useContext, useEffect, useState } from "react"
import ApiContext from "src/provider/API/call-service"
import VisibilityContext from "src/provider/state-manager/visibilityProvider"
import { AbsoluteContainer, AppSpan, AppText, CustomContainer, FlexRow, GridContainer, ScreenTitle } from "src/style"
import { AiOutlineDelete } from "react-icons/ai"
import {ReactComponent as NoDataIcon} from 'src/assets/img/no-data-icon.svg'
import { AppCenterPopupPlusD3, DecisionBox, Portal } from "src/component"

export const ScheduledTransfersD3: React.FC<any> = ({close}) => {
    const {API} = useContext(ApiContext)
    const {notifier} = useContext(VisibilityContext)
    const [activeScheduledTransfers, setActiveScheduledTransfers] = useState<any>(null)
    const [completedScheduledTransfers, setCompletedScheduledTransfers] = useState<any>(null)
    const [isDecisionBox, setIsDecisionBox] = useState(false)
    const [itemToDeleteId, setItemToDeleteId] = useState(0)
    const [navPosition, setNavPosition] = useState(0)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (!completedScheduledTransfers || !activeScheduledTransfers) handleFetchScheduledTransfers(navPosition != 0)
    }, [navPosition])

    async function handleFetchScheduledTransfers (isCompleted: boolean) {
        setIsLoading(true)
        const response = await API.getScheduledTransfers(isCompleted)
        setIsLoading(false)
        if (response) {
            navPosition === 0 ? setActiveScheduledTransfers(response) : setCompletedScheduledTransfers(response)
        }
        else close()
    }

    function scheduledTransfersToShow () : any[] {
        return navPosition === 0 ? activeScheduledTransfers : completedScheduledTransfers
    }

    function handleCancel (id: number) {
        setItemToDeleteId(id)
        setIsDecisionBox(true)
    }

    async function cancelScheduledTransfer () {
        const response = await API.cancelScheduledTransfer(itemToDeleteId)
        if (response) {
            await handleFetchScheduledTransfers(navPosition != 0)
            notifier.show('Scheduled transfer successfully cancelled', null, 'success')
        }
    }
    
    return (
        <Portal>
            <AppCenterPopupPlusD3
                title='Scheduled Transactions' 
                height='80' overflow='hidden'
                contentHeight='80'
                maxHeight='90' zIndex='6'
                close={close} 
            >
                { (isLoading) &&
                    <AbsoluteContainer top='0' left='0' zIndex='3' width='100' sizeUnit='%' height="full" radius="2">
                        <GridContainer height='100' hUnit="%" bgColor='rgba(255, 255, 255, 0.5)'>
                            <AppSpan textSize='4' color='#0D968F' className='fa fa-spinner fa-spin' />
                        </GridContainer>
                    </AbsoluteContainer> 
                }
                <ScreenTitle 
                    textSize="2" fontWeight="600"
                    color="#211121" bottomMargin="2.5"
                    className="module-row-title"
                >
                    Scheduled Transactions
                </ScreenTitle>
                <CustomContainer>
                <GridContainer 
                        height="3.6" topMargin="3" bottomMargin="2.5" 
                    >
                        <AbsoluteContainer width='50' sizeUnit="%" top="0" left={`${navPosition}`} lSizeUnit='%'>
                            <CustomContainer 
                                bgColor="#E7F6F5" height="3.6" radius="0.6" 
                                borderBottomColor='#0D968F' borderWidth='4.5'
                            />
                        </AbsoluteContainer>
                        <FlexRow zIndex="2">
                            <AppSpan 
                                color={navPosition === 0 ? "#0A756F" : "#667085"}
                                textSize="1.4" cursor="pointer" fontWeight='600'
                                width='50' sizeUnit='%' align='center'
                                hoverColor={navPosition === 0 ? '' : '#0A756F'}
                                onClick={() => setNavPosition(0)}
                            >
                                Active
                            </AppSpan>
                            
                            <AppSpan 
                                color={navPosition === 50 ? "#0A756F" : "#667085"}
                                textSize="1.4" cursor="pointer" fontWeight='600'
                                width='50' sizeUnit='%' align='center'
                                hoverColor={navPosition === 50 ? '' : '#0A756F'}
                                onClick={() => setNavPosition(50)}
                            >
                                Completed
                            </AppSpan>
                        </FlexRow>
                    </GridContainer>
                    <CustomContainer 
                        topMargin='3' height="77" hUnit="%"
                        className="scrollable-y-container"
                    >
                        {   scheduledTransfersToShow()?.map((item: any, index: number) => {
                                return (
                                    <CustomContainer
                                        minHeight="19.6" borderColor="#EAECF0" padding="2" 
                                        radius="0.8" topMargin={index != 0 ? '2' : '0'}
                                        key={index}
                                    >
                                        <FlexRow justifyContent="space-between" gap="1.5">
                                            <CustomContainer width="auto" sizeUnit="">
                                                <AppSpan>Recipient</AppSpan>
                                                <AppText fontWeight="700" color="#0D968F">{item.beneficiaryName}</AppText>
                                            </CustomContainer>
                                            { !item.isCompleted &&
                                                <AppSpan 
                                                    textSize="2" color='#98A2B3' hoverColor="#F80000"
                                                    onClick={() => handleCancel(item.id)}
                                                >
                                                    <AiOutlineDelete />
                                                </AppSpan>
                                            }
                                        </FlexRow>
                                        <FlexRow justifyContent="space-between" gap="1.5" topMargin="1.5" wrap="wrap">
                                            <GridContainer width="auto" sizeUnit="" alignItems="start">
                                                <AppSpan>Duration</AppSpan>
                                                <AppText fontWeight="700" color="#0D968F">{item.startDate}  -  {item.endDate}</AppText>
                                            </GridContainer>
                                            <GridContainer width="auto" sizeUnit="" alignItems="end">
                                                <AppSpan>Frequency</AppSpan>
                                                <AppText fontWeight="700" color="#0D968F">{item.frequency}</AppText>
                                            </GridContainer>
                                        </FlexRow>
                                        <FlexRow justifyContent="space-between" gap="1.5" topMargin="1.5" wrap="wrap">
                                            <GridContainer width="auto" sizeUnit="" alignItems="start">
                                                <AppSpan>Next Transaction Date</AppSpan>
                                                <AppText fontWeight="700" color="#0D968F">{item.nextTransferDate}</AppText>
                                            </GridContainer>
                                            <GridContainer width="auto" sizeUnit="" alignItems="end">
                                                <AppSpan>Amount</AppSpan>
                                                <AppText fontWeight="700" color="#0D968F">{item?.amountToTransfer}</AppText>
                                            </GridContainer>
                                        </FlexRow>
                                    </CustomContainer>
                                )
                            })
                        }
                        { ((navPosition === 0 && activeScheduledTransfers && !activeScheduledTransfers?.length) || (navPosition != 0 && completedScheduledTransfers && !completedScheduledTransfers?.length)) &&
                            <GridContainer topMargin="7" bottomMargin="3">
                                <CustomContainer width="auto" sizeUnit="">
                                    <NoDataIcon className="icon-component" />
                                    <AppText textSize="1.6" fontWeight="600" color="#101828" align="center" topMargin="1.5">
                                        No Record found
                                    </AppText>
                                </CustomContainer>
                            </GridContainer>
                        }
                    </CustomContainer>
                </CustomContainer>
            </AppCenterPopupPlusD3>
            { isDecisionBox &&
                <DecisionBox 
                    yesBtnText='Yes, Cancel'
                    noBtnTxt='No'
                    message='Are you sure you want to cancel the Scheduled Transanction?'
                    yesMethod={cancelScheduledTransfer}
                    close={() => setIsDecisionBox(false)} 
                />
            }
        </Portal>
    )
}